import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/contexts/AuthContext';
import { PlusIcon, ChevronDownIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { getAppSocket } from '@/lib/wesSocket';
import { confirmDialog } from '../components/ConfirmDialog';
import { hoyLocal } from '@/utils/fechaLocal';
import { agruparTareas, GRUPOS, GrupoKey, tituloGrupo, sumarDias, proximoDiaSemana, ymdDe, horaDe, isoLocal } from '@/utils/tareasFechas';
import TaskRow, { Avatar } from '@/components/tasks/TaskRow';
import TaskQuickAdd from '@/components/tasks/TaskQuickAdd';
import RoutinesView from '@/components/tasks/RoutinesView';
import type { Assignee, Routine, TaskItem } from '@/components/tasks/types';

type Vista = 'dia' | 'todas' | 'rutinas';

/**
 * Tareas v2: una sola pantalla. Alta rápida arriba, la lista agrupada por
 * cuándo vence (vencidas, hoy, mañana, esta semana…) con tilde y detalle en
 * la fila, y la pestaña Rutinas con los checklists que se repiten.
 */
const TasksPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, hasPermission } = useAuth();
  const canAssign = hasPermission('tasks_assign') || user?.role === 'admin';
  const userId = user?.id || '';
  const hoy = hoyLocal();

  const [vista, setVista] = useState<Vista>(() => {
    try { const v = localStorage.getItem('tareas-vista'); if (v === 'dia' || v === 'todas' || v === 'rutinas') return v; } catch (_) { /* sin storage */ }
    return 'dia';
  });
  const cambiarVista = (v: Vista) => { setVista(v); try { localStorage.setItem('tareas-vista', v); } catch (_) { /* sin storage */ } };

  const [tasks, setTasks] = useState<TaskItem[]>([]);
  const [assignees, setAssignees] = useState<Assignee[]>([]);
  const [routines, setRoutines] = useState<Routine[]>([]);
  const [loading, setLoading] = useState(true);
  const [persona, setPersona] = useState<string>('all');
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState<string | null>(null);
  const [cerrados, setCerrados] = useState<Record<string, boolean>>(() => {
    try { return { completadas: true, ...(JSON.parse(localStorage.getItem('tareas-grupos') || '{}')) }; } catch (_) { return { completadas: true }; }
  });
  const toggleGrupo = (k: GrupoKey) => setCerrados((prev) => {
    const next = { ...prev, [k]: !prev[k] };
    try { localStorage.setItem('tareas-grupos', JSON.stringify(next)); } catch (_) { /* sin storage */ }
    return next;
  });

  const fetchTasks = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const res = await axios.get('/api/tasks', { params: { scope: canAssign ? 'all' : 'mine', completedDays: 3 } });
      setTasks(res.data?.tasks || []);
    } catch (error: any) {
      if (!silent) toast.error(error?.response?.data?.error || 'No se pudieron cargar las tareas');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [canAssign]);

  const fetchRoutines = useCallback(async () => {
    try {
      const res = await axios.get('/api/tasks/routines');
      setRoutines(res.data?.routines || []);
    } catch (_) { /* el carril de rutinas es opcional */ }
  }, []);

  useEffect(() => {
    fetchTasks();
    fetchRoutines();
    axios.get('/api/tasks/assignees').then((r) => setAssignees(r.data?.users || [])).catch(() => setAssignees([]));
  }, [fetchTasks, fetchRoutines]);

  // Tiempo real: cambios de tareas desde otra máquina, reconexión y vuelta a la pestaña
  const refreshTimer = useRef<number | null>(null);
  useEffect(() => {
    const socket = getAppSocket();
    const schedule = (payload?: any) => {
      const path = String(payload?.path || '');
      if (path && !(path.startsWith('/api/tasks') || path.startsWith('/api/users'))) return;
      if (refreshTimer.current) window.clearTimeout(refreshTimer.current);
      refreshTimer.current = window.setTimeout(() => { fetchTasks(true); fetchRoutines(); }, 450);
    };
    const onVisible = () => { if (document.visibilityState === 'visible') schedule(); };
    socket.on('area:update', schedule);
    socket.on('connect', schedule);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      if (refreshTimer.current) window.clearTimeout(refreshTimer.current);
      socket.off('area:update', schedule);
      socket.off('connect', schedule);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [fetchTasks, fetchRoutines]);

  // ── acciones ────────────────────────────────────────────────────────
  const patch = async (task: TaskItem, cambios: Partial<TaskItem>) => {
    setTasks((prev) => prev.map((t) => (t.id === task.id ? { ...t, ...cambios } : t)));
    try {
      const res = await axios.put(`/api/tasks/${task.id}`, cambios);
      if (res.data?.task) setTasks((prev) => prev.map((t) => (t.id === task.id ? res.data.task : t)));
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'No se pudo guardar');
      fetchTasks(true);
    }
  };

  const completar = async (task: TaskItem, done: boolean) => {
    await patch(task, { status: done ? 'completed' : 'pending', completedAt: done ? new Date().toISOString() : null, completerName: done ? (user?.firstName || 'vos') : null });
    if (done) toast.success('Hecha');
  };

  const posponer = (task: TaskItem, cuando: 'manana' | 'lunes') => {
    const ymd = cuando === 'manana' ? sumarDias(hoy, 1) : proximoDiaSemana(hoy, 1);
    const hora = horaDe(task.dueAt);
    patch(task, { dueAt: isoLocal(ymd, hora && hora !== '00:00' ? hora : '09:00') });
    toast.success(cuando === 'manana' ? 'Pasada a mañana' : 'Pasada al lunes');
  };

  const eliminar = async (task: TaskItem) => {
    if (!(await confirmDialog({ title: 'Eliminar tarea', description: `"${task.title}" se borra definitivamente.`, confirmText: 'Eliminar' }))) return;
    try {
      await axios.delete(`/api/tasks/${task.id}`);
      setTasks((prev) => prev.filter((t) => t.id !== task.id));
      toast.success('Tarea eliminada');
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'No se pudo eliminar');
    }
  };

  const creada = (task: TaskItem) => {
    if (!task) { fetchTasks(true); return; }
    setTasks((prev) => [task, ...prev]);
    if (vista === 'dia' && task.assignedTo !== userId) toast.message(`Quedó en "Todas": es de ${(task.assigneeName || '').split(' ')[0]}`);
  };

  // ── datos derivados ──────────────────────────────────────────────────
  const q = search.trim().toLowerCase();
  const visibles = useMemo(() => tasks.filter((t) => {
    if (vista === 'dia' && t.assignedTo !== userId) return false;
    if (vista === 'todas' && persona !== 'all' && t.assignedTo !== persona) return false;
    if (q && !`${t.title} ${t.detail || ''} ${t.linkedLabel || ''} ${t.assigneeName || ''}`.toLowerCase().includes(q)) return false;
    return true;
  }), [tasks, vista, userId, persona, q]);
  const grupos = useMemo(() => agruparTareas(visibles, hoy), [visibles, hoy]);
  const abiertas = (list: TaskItem[]) => list.filter((t) => t.status !== 'completed' && t.status !== 'cancelled');
  const nDia = abiertas(tasks.filter((t) => t.assignedTo === userId)).length;
  const nTodas = abiertas(tasks).length;
  const hechasHoy = tasks.filter((t) => t.status === 'completed' && ymdDe(t.completedAt) === hoy).length;
  const porPersona = useMemo(() => {
    const m = new Map<string, { id: string; name: string; abiertas: number; vencidas: number }>();
    for (const t of abiertas(tasks)) {
      const cur = m.get(t.assignedTo) || { id: t.assignedTo, name: t.assigneeName || 'Sin asignar', abiertas: 0, vencidas: 0 };
      cur.abiertas += 1;
      const ymd = ymdDe(t.dueAt);
      if (ymd && ymd < hoy) cur.vencidas += 1;
      m.set(t.assignedTo, cur);
    }
    return Array.from(m.values()).sort((a, b) => b.abiertas - a.abiertas);
  }, [tasks, hoy]);
  const rutinasHoy = routines.filter((r) => r.isActive && r.aplicaHoy);
  const totalVisible = visibles.length;

  const chip = (active: boolean) => `inline-flex h-7 items-center gap-1.5 rounded-full border px-2.5 text-xs transition-colors ${active ? 'border-transparent bg-foreground text-background' : 'border-border bg-card text-muted-foreground hover:bg-accent'}`;
  const seg = (active: boolean) => `rounded-md px-3 py-1.5 text-sm transition-colors ${active ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`;

  return (
    <div className="mx-auto w-full max-w-7xl px-4 py-6 md:py-8">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Tareas</h1>
          <p className="text-sm text-muted-foreground">Lo que hay que hacer, quién lo hace y qué quedó sin hacer.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex items-center rounded-lg bg-muted p-1">
            <button type="button" onClick={() => cambiarVista('dia')} className={seg(vista === 'dia')}>Mi día{nDia > 0 && <span className="ml-1.5 rounded-full bg-accent px-1.5 text-[11px] text-muted-foreground">{nDia}</span>}</button>
            {canAssign && <button type="button" onClick={() => cambiarVista('todas')} className={seg(vista === 'todas')}>Todas{nTodas > 0 && <span className="ml-1.5 rounded-full bg-accent px-1.5 text-[11px] text-muted-foreground">{nTodas}</span>}</button>}
            <button type="button" onClick={() => cambiarVista('rutinas')} className={seg(vista === 'rutinas')}>Rutinas{rutinasHoy.length > 0 && <span className="ml-1.5 rounded-full bg-accent px-1.5 text-[11px] text-muted-foreground">{rutinasHoy.length}</span>}</button>
          </div>
          <Button type="button" variant="outline" onClick={() => navigate('/tasks/new')} className="h-9">
            <PlusIcon className="mr-1 h-4 w-4" />Tarea completa
          </Button>
        </div>
      </div>

      {vista === 'rutinas' ? (
        <div className="mt-5">
          <RoutinesView assignees={assignees} canManage={canAssign} currentUserId={userId} onChanged={() => { fetchRoutines(); fetchTasks(true); }} />
        </div>
      ) : (
        <>
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <label className="relative w-full sm:w-72">
              <MagnifyingGlassIcon className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar tarea, patente, cliente…" className="h-9 pl-8" />
            </label>
            {vista === 'todas' && canAssign && assignees.length > 1 && (
              <div className="flex flex-wrap gap-1.5">
                <button type="button" onClick={() => setPersona('all')} className={chip(persona === 'all')} aria-pressed={persona === 'all'}>Todos</button>
                {assignees.map((a) => (
                  <button key={a.id} type="button" onClick={() => setPersona(a.id)} className={chip(persona === a.id)} aria-pressed={persona === a.id}>
                    <Avatar id={a.id} name={a.name} size="sm" />{a.name.split(' ')[0]}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="mt-4 grid grid-cols-1 items-start gap-5 lg:grid-cols-[minmax(0,1fr)_300px]">
            <div className="min-w-0 space-y-3">
              <TaskQuickAdd assignees={assignees} canAssign={canAssign} defaultAssigneeId={userId} onCreated={creada} />

              {loading ? (
                <div className="py-10 text-center text-sm text-muted-foreground">Cargando tareas…</div>
              ) : totalVisible === 0 ? (
                <div className="rounded-lg border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
                  {q ? 'Nada coincide con la búsqueda.' : vista === 'dia' ? 'No tenés tareas pendientes. Escribí una arriba o mirá "Todas".' : 'Sin tareas. Escribí la primera arriba.'}
                </div>
              ) : (
                <div className="overflow-hidden rounded-lg border border-border bg-card">
                  {GRUPOS.map(({ key }) => {
                    const lista = grupos[key];
                    if (!lista.length) return null;
                    const cerrado = !!cerrados[key];
                    const esVencidas = key === 'vencidas';
                    return (
                      <section key={key} className="border-t border-border first:border-t-0">
                        <button
                          type="button"
                          onClick={() => toggleGrupo(key)}
                          className={`flex w-full items-center gap-2 bg-muted/50 px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide ${esVencidas ? 'text-red-500' : 'text-muted-foreground'}`}
                        >
                          <span>{tituloGrupo(key, hoy)}{key === 'completadas' ? ' · últimos 3 días' : ''}</span>
                          <span className="rounded-full bg-accent px-1.5 text-[11px] font-medium normal-case tracking-normal text-muted-foreground">{lista.length}</span>
                          <ChevronDownIcon className={`ml-auto h-4 w-4 transition-transform ${cerrado ? '-rotate-90' : ''}`} />
                        </button>
                        {!cerrado && lista.map((t) => (
                          <TaskRow
                            key={t.id}
                            task={t}
                            hoy={hoy}
                            assignees={assignees}
                            canAssign={canAssign}
                            currentUserId={userId}
                            expanded={expanded === t.id}
                            onToggleExpand={(id) => setExpanded((cur) => (cur === id ? null : id))}
                            onComplete={completar}
                            onPatch={patch}
                            onPostpone={posponer}
                            onDelete={eliminar}
                          />
                        ))}
                      </section>
                    );
                  })}
                </div>
              )}
            </div>

            <aside className="space-y-4">
              <div className="rounded-lg border border-border bg-card p-4">
                <h3 className="mb-2.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Hoy</h3>
                <div className="grid grid-cols-3 gap-2">
                  <div className="rounded-md border border-border px-2.5 py-2"><div className={`text-xl font-semibold leading-tight ${grupos.vencidas.length ? 'text-red-500' : ''}`}>{grupos.vencidas.length}</div><div className="text-[11px] text-muted-foreground">vencidas</div></div>
                  <div className="rounded-md border border-border px-2.5 py-2"><div className="text-xl font-semibold leading-tight">{grupos.hoy.length}</div><div className="text-[11px] text-muted-foreground">para hoy</div></div>
                  <div className="rounded-md border border-border px-2.5 py-2"><div className="text-xl font-semibold leading-tight">{hechasHoy}</div><div className="text-[11px] text-muted-foreground">hechas</div></div>
                </div>
              </div>

              <div className="rounded-lg border border-border bg-card p-4">
                <h3 className="mb-2.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Rutinas de hoy</h3>
                {rutinasHoy.length === 0 ? (
                  <p className="text-sm text-muted-foreground">{routines.length ? 'Hoy no aplica ninguna rutina activa.' : 'Sin rutinas todavía.'} <button type="button" onClick={() => cambiarVista('rutinas')} className="underline underline-offset-2">Ver rutinas</button></p>
                ) : (
                  <div className="space-y-2.5">
                    {rutinasHoy.map((r) => {
                      const h = r.hoy;
                      const pct = h && h.total ? h.pct : 0;
                      return (
                        <button key={r.id} type="button" onClick={() => cambiarVista('rutinas')} className="block w-full text-left">
                          <div className="flex justify-between text-sm"><span className="font-medium">{r.name}</span><span className="text-muted-foreground">{h && h.total ? `${h.hechas}/${h.total}` : `a las ${r.startTime || '08:00'}`}</span></div>
                          <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-muted"><div className={`h-full rounded-full ${pct === 100 ? 'bg-emerald-500' : pct > 0 ? 'bg-amber-500' : 'bg-muted-foreground/30'}`} style={{ width: `${Math.max(pct, 2)}%` }} /></div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {canAssign && porPersona.length > 0 && (
                <div className="rounded-lg border border-border bg-card p-4">
                  <h3 className="mb-2.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Por persona</h3>
                  <div className="space-y-1.5">
                    {porPersona.map((p) => (
                      <button key={p.id} type="button" onClick={() => { cambiarVista('todas'); setPersona(p.id); }} className="flex w-full items-center gap-2 text-left text-sm hover:opacity-80">
                        <Avatar id={p.id} name={p.name} size="sm" />
                        <span className="truncate">{p.name.split(' ')[0]}</span>
                        {p.vencidas > 0 && <span className="text-xs text-red-500">· {p.vencidas} vencida{p.vencidas === 1 ? '' : 's'}</span>}
                        <span className="ml-auto font-medium tabular-nums">{p.abiertas}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </aside>
          </div>
        </>
      )}
    </div>
  );
};

export default TasksPage;
